import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Eye, MousePointer, Users, Globe, Clock, Zap, Smartphone,
  LogOut, TrendingUp, MapPin, Mail, RefreshCw, ChevronDown, ChevronRight, Inbox,
} from 'lucide-react';
import { WORLD_MAP_PATH, projectLatLon } from '../utils/worldMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;
const SERVICES_API = `${BACKEND_URL}/api/services`;

const COLORS = ['#2563eb', '#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444'];

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const relativeTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const AdminDashboard = () => {
  const [tab, setTab] = useState('analytics');

  // Analytics state
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [loading, setLoading] = useState(true);

  // Clerking requests state
  const [clerking, setClerking] = useState({ items: [], total: 0 });
  const [clerkingLoading, setClerkingLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  // Map + recent visitors
  const [mapPoints, setMapPoints] = useState([]);
  const [recentVisitors, setRecentVisitors] = useState([]);

  // Precompute tooltip label per map point so this isn't re-done on every render.
  const mapPointsWithLabel = useMemo(
    () => mapPoints.map((p) => ({
      ...p,
      tooltip: [p.city, p.region, p.country].filter(Boolean).join(', '),
      key: `${p.city || ''}|${p.region || ''}|${p.country || ''}|${p.lat}|${p.lon}`,
    })),
    [mapPoints]
  );

  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [s, t, mp, rv] = await Promise.all([
        axios.get(`${ANALYTICS_API}/admin/stats?period=${period}`, { headers }),
        axios.get(`${ANALYTICS_API}/admin/timeline?period=${period}`, { headers }),
        axios.get(`${ANALYTICS_API}/admin/map-points?period=${period}`, { headers }),
        axios.get(`${ANALYTICS_API}/admin/recent-visitors?limit=5`, { headers }),
      ]);
      setStats(s.data);
      setTimeline(t.data);
      setMapPoints(mp.data?.points || []);
      setRecentVisitors(rv.data?.items || []);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  }, [period, navigate]);

  const fetchClerking = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    setClerkingLoading(true);
    try {
      const r = await axios.get(`${SERVICES_API}/admin/clerking-requests?limit=200`, { headers });
      setClerking(r.data || { items: [], total: 0 });
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setClerkingLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAnalytics();
    fetchClerking();
    const interval = setInterval(() => {
      fetchAnalytics();
      fetchClerking();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchAnalytics, fetchClerking]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const toggleRow = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading analytics...</div>
      </div>
    );
  }

  const trafficSourcesData = Object.entries(stats.traffic_sources || {}).map(([k, v]) => ({ name: k, value: v }));
  const locationsData = Object.entries(stats.locations || {}).map(([k, v]) => ({ name: k, value: v }));
  const devicesData = Object.entries(stats.devices || {}).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 p-6">
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-light text-slate-900 mb-2">Admin Dashboard</h1>
            <p className="text-slate-600">Site analytics and inbound requests</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-slate-300" data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 bg-white p-1 rounded-lg shadow-sm inline-flex mb-4">
          <Button
            onClick={() => setTab('analytics')}
            variant={tab === 'analytics' ? 'default' : 'ghost'}
            className={tab === 'analytics' ? 'bg-gradient-to-r from-blue-600 to-teal-600' : ''}
            size="sm"
            data-testid="tab-analytics"
          >
            Analytics
          </Button>
          <Button
            onClick={() => setTab('clerking')}
            variant={tab === 'clerking' ? 'default' : 'ghost'}
            className={tab === 'clerking' ? 'bg-gradient-to-r from-blue-600 to-teal-600' : ''}
            size="sm"
            data-testid="tab-clerking"
          >
            Clerking Requests
            {clerking.total > 0 && (
              <span
                className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  tab === 'clerking'
                    ? 'bg-white/25 text-white'
                    : 'bg-slate-200 text-slate-700'
                }`}
                data-testid="tab-clerking-badge"
              >
                {clerking.total}
              </span>
            )}
          </Button>
        </div>

        {tab === 'analytics' && (
          <div className="flex gap-2 bg-white p-1 rounded-lg shadow-sm inline-flex">
            {['daily', 'weekly', 'monthly', 'lifetime'].map((p) => (
              <Button
                key={p}
                onClick={() => setPeriod(p)}
                variant={period === p ? 'default' : 'ghost'}
                className={period === p ? 'bg-gradient-to-r from-blue-600 to-teal-600' : ''}
                size="sm"
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </div>
        )}
      </div>

      {tab === 'analytics' && (
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-none shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600 mb-1">Page Views</p><p className="text-3xl font-light text-slate-900">{stats.pageviews}</p></div><div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><Eye className="w-6 h-6 text-blue-600" /></div></div></CardContent></Card>
            <Card className="border-none shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600 mb-1">Clicks</p><p className="text-3xl font-light text-slate-900">{stats.clicks}</p></div><div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center"><MousePointer className="w-6 h-6 text-teal-600" /></div></div></CardContent></Card>
            <Card className="border-none shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600 mb-1">Unique Visitors</p><p className="text-3xl font-light text-slate-900">{stats.unique_visitors}</p></div><div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"><Users className="w-6 h-6 text-purple-600" /></div></div></CardContent></Card>
            <Card className="border-none shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600 mb-1">Avg. Session</p><p className="text-3xl font-light text-slate-900">{Math.floor(stats.avg_session_duration)}s</p></div><div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center"><Clock className="w-6 h-6 text-amber-600" /></div></div></CardContent></Card>
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-none shadow-lg">
              <CardHeader><CardTitle className="text-xl font-normal flex items-center gap-2"><TrendingUp className="w-5 h-5 text-blue-600" />Page Views Over Time</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="pageviews" stroke="#2563eb" strokeWidth={2} />
                    <Line type="monotone" dataKey="clicks" stroke="#14b8a6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader><CardTitle className="text-xl font-normal flex items-center gap-2"><Users className="w-5 h-5 text-purple-600" />Unique Visitors Over Time</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="visitors" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-none shadow-lg">
              <CardHeader><CardTitle className="text-xl font-normal flex items-center gap-2"><Globe className="w-5 h-5 text-teal-600" />Traffic Sources</CardTitle></CardHeader>
              <CardContent>
                {trafficSourcesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={trafficSourcesData} cx="50%" cy="50%" labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80} fill="#8884d8" dataKey="value">
                        {trafficSourcesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (<p className="text-slate-500 text-center py-12">No traffic data yet</p>)}
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader><CardTitle className="text-xl font-normal flex items-center gap-2"><Smartphone className="w-5 h-5 text-blue-600" />Device Types</CardTitle></CardHeader>
              <CardContent>
                {devicesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={devicesData} cx="50%" cy="50%" labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80} fill="#8884d8" dataKey="value">
                        {devicesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (<p className="text-slate-500 text-center py-12">No device data yet</p>)}
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader><CardTitle className="text-xl font-normal flex items-center gap-2"><MapPin className="w-5 h-5 text-amber-600" />Visitor Locations</CardTitle></CardHeader>
              <CardContent>
                {locationsData.length > 0 ? (
                  <div className="space-y-3">
                    {locationsData.slice(0, 5).map((location) => (
                      <div key={location.name} className="flex items-center justify-between">
                        <span className="text-sm text-slate-700">{location.name}</span>
                        <span className="text-sm font-medium text-slate-900">{location.value}</span>
                      </div>
                    ))}
                  </div>
                ) : (<p className="text-slate-500 text-center py-12">No location data yet</p>)}
              </CardContent>
            </Card>
          </div>

          {/* Countries Map + Recent Visitors */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-none shadow-lg lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-xl font-normal flex items-center gap-2">
                  <Globe className="w-5 h-5 text-teal-600" />
                  Where visitors come from
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-900 rounded-lg p-4" data-testid="countries-map">
                  <svg viewBox="0 0 520 260" role="img" aria-label="World map of visitor locations" className="w-full h-auto block">
                    <defs>
                      <linearGradient id="mapfade-admin" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#fff" stopOpacity="0" />
                        <stop offset=".18" stopColor="#fff" />
                        <stop offset=".9" stopColor="#fff" />
                        <stop offset="1" stopColor="#fff" stopOpacity="0" />
                      </linearGradient>
                      <mask id="mapmask-admin">
                        <rect width="520" height="200" fill="url(#mapfade-admin)" />
                      </mask>
                    </defs>
                    <path mask="url(#mapmask-admin)" d={WORLD_MAP_PATH} fill="#1B4A5C" stroke="#2A5C6C" strokeWidth=".5" strokeLinejoin="round" />
                    {mapPointsWithLabel.map((p) => {
                      if (p.lat == null || p.lon == null) return null;
                      const { x, y } = projectLatLon(p.lat, p.lon);
                      const r = Math.min(3 + Math.sqrt(p.count) * 1.6, 11);
                      return (
                        <g key={p.key}>
                          <circle cx={x} cy={y} r={r + 4} fill="#14b8a6" opacity="0.18" />
                          <circle cx={x} cy={y} r={r} fill="#14b8a6" opacity="0.85" stroke="#0B232D" strokeWidth="1" />
                          <title>{p.tooltip} — {p.count} event{p.count === 1 ? '' : 's'}</title>
                        </g>
                      );
                    })}
                    {mapPoints.length === 0 && (
                      <text x="260" y="130" textAnchor="middle" fill="#8FB3B4" fontSize="11">No mapped visitors yet</text>
                    )}
                  </svg>
                  <div className="mt-3 text-xs text-slate-400 flex items-center justify-between">
                    <span>Circle size = visits from that city</span>
                    <span>{mapPoints.length} location{mapPoints.length === 1 ? '' : 's'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-normal flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-amber-600" />
                  Recent Visitors
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentVisitors.length === 0 ? (
                  <p className="text-slate-500 text-center py-8 text-sm">No visitors yet</p>
                ) : (
                  <ul className="space-y-3" data-testid="recent-visitors">
                    {recentVisitors.map((v) => {
                      const label = [v.city, v.region].filter(Boolean).join(', ') || v.country || 'Unknown';
                      return (
                        <li key={`${v.timestamp}|${v.city || ''}|${v.country || ''}`} className="flex items-start justify-between gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{label}</div>
                            <div className="text-xs text-slate-500">{v.country || ''}</div>
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap pt-0.5">{relativeTime(v.timestamp)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-none shadow-lg">
              <CardHeader><CardTitle className="text-xl font-normal flex items-center gap-2"><Zap className="w-5 h-5 text-yellow-600" />Performance Metrics</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"><span className="text-slate-700">Avg. Page Load Speed</span><span className="text-lg font-medium text-slate-900">{Math.floor(stats.avg_load_speed)}ms</span></div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"><span className="text-slate-700">Avg. Session Duration</span><span className="text-lg font-medium text-slate-900">{Math.floor(stats.avg_session_duration)}s</span></div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"><span className="text-slate-700">Total Events Tracked</span><span className="text-lg font-medium text-slate-900">{stats.total_events}</span></div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader><CardTitle className="text-xl font-normal">Quick Stats</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg"><span className="text-slate-700">Period</span><span className="text-lg font-medium text-slate-900 capitalize">{stats.period}</span></div>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg"><span className="text-slate-700">Traffic Sources</span><span className="text-lg font-medium text-slate-900">{Object.keys(stats.traffic_sources || {}).length}</span></div>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg"><span className="text-slate-700">Countries</span><span className="text-lg font-medium text-slate-900">{Object.keys(stats.locations || {}).length}</span></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {tab === 'clerking' && (
        <div className="max-w-7xl mx-auto">
          <Card className="border-none shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Inbox className="w-5 h-5 text-teal-600" />
                Clerking Requests
                <span className="text-sm text-slate-500 font-normal ml-2">
                  {clerking.total} total
                </span>
              </CardTitle>
              <Button
                onClick={fetchClerking}
                variant="outline"
                size="sm"
                disabled={clerkingLoading}
                data-testid="clerking-refresh"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${clerkingLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {clerking.items.length === 0 ? (
                <div className="text-center py-16">
                  <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No requests yet</p>
                  <p className="text-slate-500 text-sm mt-1">
                    When someone submits the form on your site, it'll show up here.
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr className="text-left">
                        <th className="w-8 py-3 px-3"></th>
                        <th className="py-3 px-3 font-medium">Date</th>
                        <th className="py-3 px-3 font-medium">Name</th>
                        <th className="py-3 px-3 font-medium">Email</th>
                        <th className="py-3 px-3 font-medium">Company</th>
                        <th className="py-3 px-3 font-medium">Format</th>
                        <th className="py-3 px-3 font-medium">Days</th>
                        <th className="py-3 px-3 font-medium">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clerking.items.map((it) => {
                        const isOpen = !!expanded[it.id];
                        return (
                          <React.Fragment key={it.id}>
                            <tr
                              className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                              onClick={() => toggleRow(it.id)}
                              data-testid={`clerking-row-${it.id}`}
                            >
                              <td className="py-3 px-3 text-slate-400">
                                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </td>
                              <td className="py-3 px-3 text-slate-700 whitespace-nowrap">{formatDate(it.created_at)}</td>
                              <td className="py-3 px-3 text-slate-900 font-medium">{it.name}</td>
                              <td className="py-3 px-3 text-slate-700">
                                <a
                                  className="text-blue-600 hover:underline"
                                  href={`mailto:${it.email}?subject=${encodeURIComponent(`Re: Clerking request from ${it.name}`)}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {it.email}
                                </a>
                              </td>
                              <td className="py-3 px-3 text-slate-700">{it.company || '—'}</td>
                              <td className="py-3 px-3 text-slate-700">{it.format || '—'}</td>
                              <td className="py-3 px-3 text-slate-700">{it.days ?? '—'}</td>
                              <td className="py-3 px-3 text-slate-700">{it.category || '—'}</td>
                            </tr>
                            {isOpen && (
                              <tr className="border-t border-slate-100 bg-slate-50/60">
                                <td></td>
                                <td colSpan={7} className="py-4 px-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                    <div><span className="text-slate-500">Phone:</span> <span className="text-slate-800">{it.phone || '—'}</span>{it.text_ok && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-teal-100 text-teal-700">Text OK</span>}</div>
                                    <div><span className="text-slate-500">Start date:</span> <span className="text-slate-800">{it.start_date || '—'}</span></div>
                                    <div className="md:col-span-2"><span className="text-slate-500">Location:</span> <span className="text-slate-800">{it.location || '—'}</span></div>
                                    <div><span className="text-slate-500">Hours:</span> <span className="text-slate-800">{it.hours || '—'}</span></div>
                                    <div><span className="text-slate-500">Auction days:</span> <span className="text-slate-800">{it.days ?? '—'}</span></div>
                                  </div>
                                  <div className="mt-4">
                                    <div className="text-xs text-slate-500 font-medium mb-1">Message</div>
                                    <div className="p-3 bg-white rounded border-l-4 border-teal-500 text-slate-800 whitespace-pre-wrap">{it.message}</div>
                                  </div>
                                  <div className="mt-4">
                                    <a
                                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-slate-900 text-white text-sm hover:bg-slate-800"
                                      href={`mailto:${it.email}?subject=${encodeURIComponent(`Re: Clerking request from ${it.name}`)}`}
                                    >
                                      <Mail className="w-4 h-4" /> Reply to {it.name.split(' ')[0]}
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
